import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Max summary/drawer height ratio before the summary un-sticks.
   * Raised from stock 0.5: with upsells relocated to the scroll region the
   * summary (totals + trust + checkout + express) is ~0.6-0.7 of the drawer on
   * desktop — keep it pinned there. On small phones it exceeds this and
   * un-sticks; cart-sticky-checkout.js provides the pinned CTA bar instead.
   * @type {number}
   */
  #summaryThreshold = 0.75;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd, { signal });
    document.addEventListener('cart:add', this.#handleCartAdd, { signal });
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState, { signal });
    this.querySelector('[data-cart-drawer-trigger]')?.addEventListener('click', this.#handleTriggerClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open') || event.detail?.data?.didError) return;

    requestAnimationFrame(() => this.open());
  };

  #handleTriggerClick = (event) => {
    event.preventDefault();
    this.open();
  };

  #resetScrollPosition = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const scrollRegion = dialog?.querySelector('.cart-drawer__items');

    if (scrollRegion) scrollRegion.scrollTop = 0;
  };

  open() {
    // Reset before opening to avoid flashing the previously scrolled position.
    this.#resetScrollPosition();
    this.showDialog();
    requestAnimationFrame(this.#resetScrollPosition);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
